import type { AreaComponent, Surface } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useStore } from 'zustand';
import { findInitialPlacementPosition, useEditAreaDraftStore } from '@/entities/edit-area';
import { Button } from '@/shared/ui';
import { applyEditAreaCommand } from '../model/apply-edit-area-command';
import { describeNoRoom } from '../model/describe-no-room';

interface PlaceOnFaceProps {
  /** 이 면에 아직 없는 선택 영역. 호출자는 이 영역의 id 를 key 로 줘서, 선택이 바뀌면 지난 판정을 버린다(설계 §5-6). */
  readonly component: AreaComponent;
  /** 배치할 표면. 없으면 버튼이 꺼진다. */
  readonly surface: Surface | undefined;
  readonly disabled: boolean;
}

/** 마지막 '자리 없음' 판정을 계산한 정확한 입력. 이 표면·이 인쇄 영역이 아니게 되면 더 이상 유효하지 않다. */
interface PlacementFailure {
  readonly component: AreaComponent;
  readonly surface: Surface;
  readonly message: string;
}

/** 인스펙터의 [이 면에 배치] — 가운데 → 격자 탐색으로 첫 자리를 찾아 놓고, 없으면 그 사유를 보인다. */
export const PlaceOnFace = ({ component, surface, disabled }: PlaceOnFaceProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const [failure, setFailure] = useState<PlacementFailure | null>(null);
  // 표면·컴포넌트는 커맨드마다 새 객체로 교체되므로, 참조가 바뀌면 안내를 계산했던 입력이 더 이상 아니다.
  const error =
    failure && failure.component === component && failure.surface === surface
      ? failure.message
      : null;
  const place = (): void => {
    if (surface === undefined) return;
    const position = findInitialPlacementPosition(component, surface);
    if (position === null) {
      setFailure({ component, surface, message: describeNoRoom(component, surface) });
      return;
    }
    const rejection = applyEditAreaCommand(store, () =>
      state.upsertPlacement(state.selection.face, {
        componentId: component.id,
        surfaceId: surface.id,
        position,
      }),
    );
    setFailure(rejection === null ? null : { component, surface, message: rejection });
  };
  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        disabled={disabled || surface === undefined}
        onClick={place}
      >
        이 면에 배치
      </Button>
      {surface === undefined ? (
        <p className="text-sm text-muted-foreground">표면을 먼저 추출해 주세요.</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
